import * as z from "zod";

export const INTERNATIONAL_CUSTOMS_CSV_COLUMNS = {
  SOURCE_ID: "Source ID",
  SKU: "SKU",
  HS_CODE: "HS Code",
  COUNTRY_OF_ORIGIN: "Country of Origin",
  PRODUCT_DESCRIPTION: "Product Description",
  WEIGHT: "Weight",
  WEIGHT_UNIT: "Weight Unit",
  MANUFACTURER_NAME: "Manufacturer Name",
  MANUFACTURER_MID: "Manufacturer MID",
  MANUFACTURER_STREET_1: "Manufacturer Street 1",
  MANUFACTURER_STREET_2: "Manufacturer Street 2",
  MANUFACTURER_CITY: "Manufacturer City",
  MANUFACTURER_STATE: "Manufacturer State",
  MANUFACTURER_POSTAL_CODE: "Manufacturer Postal Code",
  MANUFACTURER_COUNTRY: "Manufacturer Country",
} as const;

export const INTERNATIONAL_CUSTOMS_WEIGHT_UNITS = [
  "g",
  "kg",
  "oz",
  "lb",
] as const;
export type InternationalCustomsWeightUnit =
  (typeof INTERNATIONAL_CUSTOMS_WEIGHT_UNITS)[number];

export const INTERNATIONAL_CUSTOMS_CSV_COLUMN_NAMES: readonly string[] =
  Object.values(INTERNATIONAL_CUSTOMS_CSV_COLUMNS);

const COLS = INTERNATIONAL_CUSTOMS_CSV_COLUMNS;

export interface InternationalCustomsFieldSpec {
  column: string;
  requirement: "required" | "conditional" | "optional";
  format: string;
}

export const INTERNATIONAL_CUSTOMS_CSV_FIELD_SPECIFICATIONS: readonly InternationalCustomsFieldSpec[] =
  [
    {
      column: COLS.SOURCE_ID,
      requirement: "conditional",
      format:
        "Platform source ID for the variant. For Shopify, must be the full GID (e.g. gid://shopify/ProductVariant/43689149456789). Required if SKU is empty.",
    },
    {
      column: COLS.SKU,
      requirement: "conditional",
      format: "Up to 255 characters. Required if Source ID is empty.",
    },
    {
      column: COLS.HS_CODE,
      requirement: "optional",
      format: "Up to 50 characters (e.g. 6109.10.00).",
    },
    {
      column: COLS.COUNTRY_OF_ORIGIN,
      requirement: "optional",
      format: "ISO 3166-1 alpha-2 country code (e.g. US, PT, VN).",
    },
    {
      column: COLS.PRODUCT_DESCRIPTION,
      requirement: "optional",
      format:
        "Up to 45 characters. Note: some carriers (including USPS) only allow 30 characters — descriptions will be automatically trimmed to fit when needed, so keep the most important detail at the start.",
    },
    {
      column: COLS.WEIGHT,
      requirement: "conditional",
      format: `Positive number with up to 2 decimals (e.g. 12.50). Required when "${COLS.WEIGHT_UNIT}" is set.`,
    },
    {
      column: COLS.WEIGHT_UNIT,
      requirement: "conditional",
      format: `One of ${INTERNATIONAL_CUSTOMS_WEIGHT_UNITS.map((u) => `"${u}"`).join(", ")}. Required when "${COLS.WEIGHT}" is set.`,
    },
    {
      column: COLS.MANUFACTURER_NAME,
      requirement: "conditional",
      format:
        "Up to 255 characters. Required when any other manufacturer field is set.",
    },
    {
      column: COLS.MANUFACTURER_MID,
      requirement: "conditional",
      format:
        "Alphanumeric, uppercase, up to 15 characters. Required if no manufacturer address is provided.",
    },
    {
      column: COLS.MANUFACTURER_STREET_1,
      requirement: "conditional",
      format: "Up to 255 characters. Required if MID is not provided.",
    },
    {
      column: COLS.MANUFACTURER_STREET_2,
      requirement: "optional",
      format: "Up to 255 characters.",
    },
    {
      column: COLS.MANUFACTURER_CITY,
      requirement: "conditional",
      format: "Up to 255 characters. Required if MID is not provided.",
    },
    {
      column: COLS.MANUFACTURER_STATE,
      requirement: "optional",
      format: "Up to 255 characters.",
    },
    {
      column: COLS.MANUFACTURER_POSTAL_CODE,
      requirement: "optional",
      format: "Up to 50 characters.",
    },
    {
      column: COLS.MANUFACTURER_COUNTRY,
      requirement: "conditional",
      format:
        "ISO 3166-1 alpha-2 country code. Required if MID is not provided.",
    },
  ];

const CountryCodeSchema = z
  .string()
  .regex(/^[A-Z]{2}$/, "Must be a valid ISO 3166-1 alpha-2 country code");

const ManufacturerMidSchema = z
  .string()
  .toUpperCase()
  .regex(/^[A-Z0-9]+$/, "MID must be alphanumeric")
  .max(15, "MID must be at most 15 characters");

const WEIGHT_MAX = 99_999_999.99;
const WeightSchema = z.preprocess(
  (val) => {
    if (val == null) {
      return undefined;
    }
    if (typeof val === "number") {
      return val;
    }
    if (typeof val === "string") {
      const trimmed = val.trim();
      if (trimmed === "") {
        return undefined;
      }
      const num = Number(trimmed);
      return Number.isFinite(num) ? num : val;
    }
    return val;
  },
  z
    .number()
    .positive(`"${COLS.WEIGHT}" must be a positive number`)
    .max(WEIGHT_MAX, `"${COLS.WEIGHT}" exceeds maximum allowed value`)
    .refine((n) => Number(n.toFixed(2)) === n, {
      message: `"${COLS.WEIGHT}" supports at most 2 decimal places`,
    }),
);

const WeightUnitSchema = z.preprocess(
  (val) => (typeof val === "string" ? val.trim().toLowerCase() : val),
  z.enum(INTERNATIONAL_CUSTOMS_WEIGHT_UNITS, {
    message: `"${COLS.WEIGHT_UNIT}" must be one of ${INTERNATIONAL_CUSTOMS_WEIGHT_UNITS.map((u) => `"${u}"`).join(", ")}`,
  }),
);

const InternationalCustomsRow_ = z
  .object({
    sourceId: z.string().max(255).optional(),
    sku: z.string().max(255).optional(),
    hsCode: z.string().max(50).optional(),
    countryOfOrigin: CountryCodeSchema.optional(),
    productDescription: z
      .string()
      .max(45, `"${COLS.PRODUCT_DESCRIPTION}" must be 45 characters or fewer`)
      .optional(),
    weight: WeightSchema.optional(),
    weightUnit: WeightUnitSchema.optional(),
    manufacturerName: z.string().max(255).optional(),
    manufacturerMid: ManufacturerMidSchema.optional(),
    manufacturerStreet1: z.string().max(255).optional(),
    manufacturerStreet2: z.string().max(255).optional(),
    manufacturerCity: z.string().max(255).optional(),
    manufacturerState: z.string().max(255).optional(),
    manufacturerPostalCode: z.string().max(50).optional(),
    manufacturerCountry: CountryCodeSchema.optional(),
  })
  .refine((r) => r.sourceId || r.sku, {
    message: `Each row must include "${COLS.SOURCE_ID}" or "${COLS.SKU}"`,
  })
  .refine((r) => (r.weight === undefined) === (r.weightUnit === undefined), {
    message: `"${COLS.WEIGHT}" and "${COLS.WEIGHT_UNIT}" must be provided together`,
  })
  .refine(
    (r) => {
      const hasManufacturerDetail =
        r.manufacturerMid ||
        r.manufacturerStreet1 ||
        r.manufacturerStreet2 ||
        r.manufacturerCity ||
        r.manufacturerState ||
        r.manufacturerPostalCode ||
        r.manufacturerCountry;
      return !hasManufacturerDetail || !!r.manufacturerName;
    },
    {
      message: `"${COLS.MANUFACTURER_NAME}" is required when any other manufacturer field is set`,
    },
  )
  .refine(
    (r) => {
      if (!r.manufacturerName) {
        return true;
      }
      if (r.manufacturerMid) {
        return true;
      }
      return !!(
        r.manufacturerStreet1 &&
        r.manufacturerCity &&
        r.manufacturerCountry
      );
    },
    {
      message: `Manufacturer requires "${COLS.MANUFACTURER_MID}" or address ("${COLS.MANUFACTURER_STREET_1}", "${COLS.MANUFACTURER_CITY}", "${COLS.MANUFACTURER_COUNTRY}")`,
    },
  );

export const InternationalCustomsRow: z.ZodType<InternationalCustomsRow> =
  InternationalCustomsRow_;
export interface InternationalCustomsRow
  extends z.infer<typeof InternationalCustomsRow_> {}

export type InternationalCustomsRowResultStatus =
  | "updated"
  | "not_found"
  | "skipped"
  | "error";

export const InternationalCustomsRowResult = z.object({
  status: z.enum(["updated", "not_found", "skipped", "error"]),
  rowNumber: z.number().int().positive(),
  identifier: z.string(),
  message: z.string().optional(),
});
export type InternationalCustomsRowResult = z.infer<
  typeof InternationalCustomsRowResult
>;

export const ImportInternationalCustomsCsvCounts = z.object({
  totalCount: z.number().int().nonnegative(),
  updatedCount: z.number().int().nonnegative(),
  notFoundCount: z.number().int().nonnegative(),
  skippedCount: z.number().int().nonnegative(),
  errorCount: z.number().int().nonnegative(),
});
export type ImportInternationalCustomsCsvCounts = z.infer<
  typeof ImportInternationalCustomsCsvCounts
>;

// Workflow output stays small enough to fit in Temporal's payload cap by
// pointing at S3 instead of inlining the per-row results.
export const ImportInternationalCustomsCsvWorkflowOutput =
  ImportInternationalCustomsCsvCounts.extend({ resultsS3Key: z.string() });
export type ImportInternationalCustomsCsvWorkflowOutput = z.infer<
  typeof ImportInternationalCustomsCsvWorkflowOutput
>;

// RPC response — server hydrates `results` from S3 so the modal can render
// per-row outcomes without changing the existing client contract.
export const ImportInternationalCustomsCsvResult =
  ImportInternationalCustomsCsvCounts.extend({
    results: z.array(InternationalCustomsRowResult),
  });
export type ImportInternationalCustomsCsvResult = z.infer<
  typeof ImportInternationalCustomsCsvResult
>;
